/**
 *
 * Milestones
 *
 */
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { RoadmapHeader } from 'app/components/Header/RoadmapHeader';
import { View, Flex, Heading, Grid } from '@adobe/react-spectrum';
import LeftSidebar from 'app/components/RoadmapCard/LeftSidebar';
import { Legend } from 'app/components/RoadmapCard/Legend';
import { ParkingLot } from 'app/components/RoadmapCard/ParkingLot';
import { PARKING_LOT_GRID_COLUMN_PX } from 'app/components/RoadmapCard/parkingLotLayoutConstants';
import { useSelector, useDispatch } from 'react-redux';
import { selectLayout } from 'app/pages/Layout/slice/selectors';
import {
  selectParkingLot,
  selectHome,
  selectFullScreenMode
} from 'app/pages/Home/slice/selectors';
import { selectMilestonesData } from './slice/selectors';
import { selectIsAuthenticated } from 'app/pages/Layout/slice/selectors';
import { useHomeSlice } from 'app/pages/Home/slice';
import { useSuccessAlert } from 'app/hooks/useSuccessAlert';
import {
  getInitialDateRange,
  saveUserPreferences,
  getUserPreferences
} from 'utils/helpers';
import { useViewStateDeepLink } from 'app/hooks/useViewStateDeepLink';
import {
  parseViewStateFromSearchParams,
  ViewStateParams
} from 'utils/viewStateDeepLink';
import {
  isSharedViewPageLoad,
  isSharedViewMode,
  hasSharedViewUrlStateBeenApplied,
  shouldPreferUrlOverLocalPreferences
} from 'utils/sharedViewMode';
import { useLocation } from 'react-router-dom';
import { useMilestonesGridSlice } from './slice';
import {
  selectSortSelections,
  selectIsMetaTableCollapsed
} from './slice/selectors';
import styles from './Milestones.module.scss';
import SplitScrollTable from './SplitScrollTable';
import { UrlSlideoutManager } from 'app/components/UrlSlideoutManager';
import { useFilteredParkingLot } from './hooks';
import { ViewStateProvider } from 'app/context/ViewStateContext';

export const MilestonesGridReport = memo(() => {
  // Initialize the home slice to ensure Legend state is available
  const { actions } = useHomeSlice();
  const { actions: milestonesActions } = useMilestonesGridSlice();
  const dispatch = useDispatch();
  const { isSidebarOpen } = useSelector(selectLayout);
  const showParkingLot = useSelector(selectParkingLot);
  const milestonesData = useSelector(selectMilestonesData);
  const homeState = useSelector(selectHome);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isFullScreenMode = useSelector(selectFullScreenMode);
  const sortSelections = useSelector(selectSortSelections);
  const isMetaTableCollapsed = useSelector(selectIsMetaTableCollapsed);
  const [showPhaseDatesOnly, setShowPhaseDatesOnly] = useState(false);
  const [betaViewInitialized, setBetaViewInitialized] = useState(false);
  // Render table only after one-time initial date setup (avoids double API call)
  const [dateRangeReady, setDateRangeReady] = useState(false);
  const location = useLocation();

  // Use success alert hook
  const { handleMomentCreateSuccess, SuccessAlertBanner } = useSuccessAlert();

  // Deep link hook for milestones grid view state
  const { hasUrlState, getUrlState } = useViewStateDeepLink({
    autoApplyOnMount: true,
    autoSyncToUrl: false,
    onApplyUrlState: (params: ViewStateParams) => {
      // Apply view state from URL to both Home and Milestones Grid slices
      dispatch(actions.applyViewStateFromUrl(params));
      dispatch(milestonesActions.applyViewStateFromUrl(params));

      // Apply beta view toggle from URL
      if (params.showBetaView !== undefined) {
        setShowPhaseDatesOnly(params.showBetaView);
        setBetaViewInitialized(true);
      }
    },
    getCurrentViewState: () => {
      // Get current state for generating deep links
      return {
        // Home state (filters, toggles, dates)
        startDate: homeState.selectedDates?.start,
        endDate: homeState.selectedDates?.end,
        productGroups: homeState.productGroups.selectedGroups,
        categories: homeState.categories.selectedCats,
        audiences: homeState.audiences.selectedAudiences,
        features: homeState.features.selectedFeatures,
        status: homeState.status.selectedStatus,
        tiers: homeState.lanesTiers.selectedLanesTiers,
        products: homeState.productFilter.selectedProducts,
        showParkingLot: homeState.parkingLot,
        // Milestones grid specific state
        sortColumn: sortSelections.sharedSortColumn,
        sortOption: sortSelections.sharedSort,
        pinColumn: sortSelections.sharedPinColumn,
        pinOption: sortSelections.sharedPin,
        isMetaTableCollapsed: isMetaTableCollapsed,
        showBetaView: showPhaseDatesOnly
      };
    }
  });

  // Apply filters to parking lot data using the same filter logic as Milestones Grid
  // Parking lot (TBD, In the works) comes from milestones-grid-report API inTheWorks
  const filteredParkingLotData = useFilteredParkingLot(
    milestonesData?.inTheWorks
  );

  // Apply beta view from URL on deep-link load (skip on in-session shared-view route hops)
  useEffect(() => {
    const params = parseViewStateFromSearchParams(location.search);
    if (params.showBetaView === undefined) {
      return;
    }

    if (isSharedViewMode() && hasSharedViewUrlStateBeenApplied()) {
      return;
    }

    setShowPhaseDatesOnly(params.showBetaView);
    setBetaViewInitialized(true);
  }, [location.search]);

  // Load beta view preference from localStorage on mount (only if no deep link)
  useEffect(() => {
    if (!betaViewInitialized && !shouldPreferUrlOverLocalPreferences()) {
      const preferences = getUserPreferences();
      if (preferences.showBetaView !== undefined) {
        setShowPhaseDatesOnly(preferences.showBetaView);
      }
      setBetaViewInitialized(true);
    }
  }, [betaViewInitialized]);

  // Save beta view preference to localStorage when it changes
  const handleBetaToggle = (value: boolean) => {
    setShowPhaseDatesOnly(value);
    saveUserPreferences({ showBetaView: value });
  };

  // Function to get current view state for Share View button
  const getCurrentViewState = React.useCallback(() => {
    // Determine which sort is active and format it correctly
    let sortColumn: string | undefined;
    let sortOption: string | undefined;

    // Check if name column has a sort
    if (sortSelections.name && sortSelections.name.length > 0) {
      sortColumn = 'name';
      sortOption = sortSelections.name[0]; // Name sort is an array but only has one item
    }
    // Otherwise check if shared columns have a sort
    else if (sortSelections.sharedSortColumn && sortSelections.sharedSort) {
      sortColumn = sortSelections.sharedSortColumn;
      sortOption = sortSelections.sharedSort;
    }

    return {
      // Home state (filters, toggles, dates)
      startDate: homeState.selectedDates?.start,
      endDate: homeState.selectedDates?.end,
      productGroups: homeState.productGroups.selectedGroups,
      categories: homeState.categories.selectedCats,
      audiences: homeState.audiences.selectedAudiences,
      features: homeState.features.selectedFeatures,
      status: homeState.status.selectedStatus,
      tiers: homeState.lanesTiers.selectedLanesTiers,
      products: homeState.productFilter.selectedProducts,
      genAiFilters: homeState.genAiFilter.selectedFilters,
      revenueImpactFilters: homeState.revenueImpactFilter.selectedFilters,
      momentTypeFilters: homeState.momentTypeFilter.selectedMomentTypes,
      categoryProductCategories:
        homeState.categoryProductFilter.selectedCategories,
      categoryProductProducts: homeState.categoryProductFilter.selectedProducts,
      showParkingLot: homeState.parkingLot,
      // Milestones grid specific state
      sortColumn,
      sortOption,
      pinColumn: sortSelections.sharedPinColumn,
      pinOption: sortSelections.sharedPin,
      isMetaTableCollapsed: isMetaTableCollapsed,
      showBetaView: showPhaseDatesOnly
    };
  }, [
    homeState.selectedDates,
    homeState.productGroups.selectedGroups,
    homeState.categories.selectedCats,
    homeState.audiences.selectedAudiences,
    homeState.features.selectedFeatures,
    homeState.status.selectedStatus,
    homeState.lanesTiers.selectedLanesTiers,
    homeState.productFilter.selectedProducts,
    homeState.genAiFilter.selectedFilters,
    homeState.revenueImpactFilter.selectedFilters,
    homeState.momentTypeFilter.selectedMomentTypes,
    homeState.categoryProductFilter.selectedCategories,
    homeState.categoryProductFilter.selectedProducts,
    homeState.parkingLot,
    sortSelections.name,
    sortSelections.sharedSortColumn,
    sortSelections.sharedSort,
    sortSelections.sharedPinColumn,
    sortSelections.sharedPin,
    isMetaTableCollapsed,
    showPhaseDatesOnly
  ]);

  // Define custom lanes/tiers options for Milestones Grid Report
  // Excluding Key Dates and Events
  const customLanesTiersOptions = useMemo(
    () => [
      // { key: 'price-changes-and-offers', label: 'Price Changes and Offers' },
      { key: 'l1', label: 'L1' },
      { key: 'l2', label: 'L2' },
      { key: 'l3', label: 'L3' },
      { key: 'all-other-releases', label: 'All Other Releases' }
      // { key: 'tbd', label: 'TBD' }
    ],
    []
  );

  // One-time initial date: URL > saved preferences > default (today .. +6 months).
  // Only then allow table to mount so it loads once with the correct range.
  useLayoutEffect(() => {
    if (!isAuthenticated) return;
    const urlHasState = hasUrlState() || isSharedViewPageLoad();
    const urlState = urlHasState ? getUrlState() : null;
    const initialDateRange =
      urlState?.startDate && urlState?.endDate
        ? { start: urlState.startDate, end: urlState.endDate }
        : getInitialDateRange(); // preferences if set, else default
    if (!urlHasState) {
      dispatch(actions.setDateRange(initialDateRange));
    }
    setDateRangeReady(true); // allow table to mount (same whether URL, preference, or no preference)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hasUrlState, getUrlState]);

  return (
    <ViewStateProvider getCurrentViewState={getCurrentViewState}>
      <View data-testid="milestones-page-container">
        <RoadmapHeader
          onMomentCreateSuccess={handleMomentCreateSuccess}
          showViewControls={true}
          hideEventsFilter={true}
          milestonesGridMomentTypeMenu={true}
          hideCompactToggle={true}
          hideIconsToggle={true}
          showBetaToggle={true}
          betaToggleLabel="Beta"
          betaToggleSelected={showPhaseDatesOnly}
          onBetaToggle={handleBetaToggle}
          customLanesTiersOptions={customLanesTiersOptions}
        />
        {SuccessAlertBanner}
        <Grid
          areas={
            showParkingLot ? ['sidebar content parking'] : ['sidebar content']
          }
          columns={
            showParkingLot
              ? [
                  isFullScreenMode ? '0px' : !isSidebarOpen ? '40px' : '160px',
                  '1fr',
                  `${PARKING_LOT_GRID_COLUMN_PX}px`
                ]
              : [
                  isFullScreenMode ? '0px' : !isSidebarOpen ? '40px' : '160px',
                  '1fr',
                  '0px'
                ]
          }
          rows={['minmax(0, 1fr)']}
          UNSAFE_className={`${styles['milestones-grid']} ${
            isFullScreenMode ? styles['fullscreen'] : ''
          }`}
        >
          {!isFullScreenMode && <LeftSidebar />}
          <View
            gridArea="content"
            UNSAFE_className={`${styles['content-container']} ${
              isFullScreenMode ? styles['fullscreen'] : ''
            }`}
          >
            {dateRangeReady && (
              <SplitScrollTable showPhaseDatesOnly={showPhaseDatesOnly} />
            )}
          </View>
          {showParkingLot && (
            <View
              gridArea="parking"
              UNSAFE_className={`${styles['parking-lot']} ${
                isFullScreenMode ? styles['fullscreen'] : ''
              }`}
            >
              <ParkingLot
                showParkingLot={showParkingLot}
                data={filteredParkingLotData}
                isFullScreenMode={isFullScreenMode}
              />
            </View>
          )}
        </Grid>
        <Legend />
        <UrlSlideoutManager />
      </View>
    </ViewStateProvider>
  );
});
